import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, AuthState, UserService } from '@ipg/ngx-frontend-toolset';
import { switchMap, tap } from 'rxjs';
import { User } from '../../../models/user.model';
import { AuthViewComponent } from '../components/auth-view/auth-view.component';

@Component({
  selector: 'app-support-login',
  imports: [AuthViewComponent, MatButton, MatProgressSpinner],
  templateUrl: './support-login.component.html',
  styleUrl: './support-login.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupportLoginComponent {
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _auth = inject(AuthService);
  private readonly _userService = inject<UserService<User>>(UserService);

  protected readonly status = signal<'loading' | 'error'>('loading');
  protected readonly errorMessage = signal<string | null>(null);

  constructor() {
    const urlParams = new URLSearchParams(window.location.search);
    const supportToken =
      urlParams.get('support_token') ||
      urlParams.get('token') ||
      this._activatedRoute.snapshot.queryParamMap.get('support_token') ||
      this._activatedRoute.snapshot.queryParamMap.get('token');

    const refreshToken =
      urlParams.get('refresh_token') ||
      this._activatedRoute.snapshot.queryParamMap.get('refresh_token') ||
      supportToken;

    if (!supportToken) {
      this.status.set('error');
      this.errorMessage.set('Geen support-token gevonden in de URL.');
      return;
    }

    this._auth.authDB
      .pipe(
        switchMap((db) =>
          db.set('authTokens', {
            accessToken: supportToken,
            refreshToken: refreshToken,
          })
        ),
        tap(() => {
          (this._auth as any)._authState?.stateSubject?.next(AuthState.Authorized);
        }),
        switchMap(() => this._userService.fetch())
      )
      .subscribe({
        next: () => {
          this._router.navigate(['/']);
        },
        error: () => {
          (this._auth as any)._authState?.stateSubject?.next(AuthState.Unauthorized);
          this.status.set('error');
          this.errorMessage.set(
            'De support-sessie token is ongeldig of verlopen. Start de sessie opnieuw vanuit het CRM.'
          );
        },
      });
  }

  protected goToLogin(): void {
    this._router.navigate(['/login']);
  }
}
